import { z } from 'zod';

/* ─────────────────────────────────────────────────────────────────────
   Leitura executiva do Board — a visão da liderança sobre o negócio.
   Uma entrevista por pessoa da liderança, as mesmas 16 perguntas para
   todos. As respostas se somam aos documentos e ao resto do setup no
   contexto estratégico inicial que os agentes consultam.
   ───────────────────────────────────────────────────────────────────── */

export interface BoardQuestion { id: BoardQuestionId; no: number; text: string; hint?: string }

export const BOARD_SECTIONS = [
  { key: 'negocio', title: 'Negócio e posicionamento', questions: [
    { id: 'q1', text: 'Em uma frase, qual é o negócio desta organização?', hint: 'O que ela realmente entrega e para quem?' },
    { id: 'q2', text: 'Por que clientes, alunos ou usuários escolhem esta organização hoje?', hint: 'Quais são, na sua percepção, os principais motivos de escolha?' },
    { id: 'q3', text: 'O que a organização faz particularmente bem e que seria difícil para um concorrente reproduzir?' },
  ] },
  { key: 'mercado', title: 'Mercado e clientes', questions: [
    { id: 'q4', text: 'Qual é a principal mudança no mercado que pode afetar a organização nos próximos 3 a 5 anos?' },
    { id: 'q5', text: 'O que mudou recentemente no comportamento, nas expectativas ou nas necessidades dos seus clientes, alunos ou usuários?' },
    { id: 'q6', text: 'Quem são hoje os concorrentes ou alternativas que mais pressionam a organização?', hint: 'Considere também novos modelos, tecnologias ou soluções que não eram concorrentes tradicionais.' },
  ] },
  { key: 'desempenho', title: 'Desempenho e operação', questions: [
    { id: 'q7', text: 'Quais são hoje os três principais fatores que limitam o crescimento ou a evolução da organização?' },
    { id: 'q8', text: 'Em quais áreas você percebe maior desperdício de tempo, recursos ou capacidade?' },
    { id: 'q9', text: 'Quais indicadores você considera indispensáveis para saber se a organização está indo bem?' },
  ] },
  { key: 'pessoas', title: 'Pessoas e organização', questions: [
    { id: 'q10', text: 'A estrutura, as competências e as lideranças atuais são suficientes para executar a estratégia dos próximos anos?', hint: 'O que precisaria mudar ou evoluir?' },
    { id: 'q11', text: 'Que característica da cultura organizacional mais contribui para o desempenho? E qual mais dificulta mudanças?' },
  ] },
  { key: 'inovacao', title: 'Inovação e tecnologia', questions: [
    { id: 'q12', text: 'Em quais áreas tecnologia, dados ou Inteligência Artificial poderiam gerar maior impacto no negócio hoje?' },
    { id: 'q13', text: 'O que a organização deveria estar fazendo diferente, mas ainda não conseguiu colocar em prática?' },
  ] },
  { key: 'futuro', title: 'Futuro e prioridades', questions: [
    { id: 'q14', text: 'Se estivermos reunidos novamente daqui a três anos e você considerar que este período foi um grande sucesso, o que terá mudado concretamente?' },
    { id: 'q15', text: 'Se o Board pudesse definir apenas três prioridades para os próximos 12 meses, quais seriam?' },
  ] },
  { key: 'final', title: 'Pergunta final', questions: [
    { id: 'q16', text: 'O que não perguntamos e você considera fundamental para compreender esta organização?' },
  ] },
] as const;

export const BOARD_QUESTION_IDS = [
  'q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10', 'q11', 'q12', 'q13', 'q14', 'q15', 'q16',
] as const;
export type BoardQuestionId = (typeof BOARD_QUESTION_IDS)[number];

export const BOARD_QUESTIONS: ReadonlyArray<BoardQuestion & { section: string }> = BOARD_SECTIONS.flatMap(s =>
  s.questions.map(q => ({ ...q, section: s.title, no: Number(q.id.slice(1)) })));

/** Limite de cada resposta: o pedido é de 3 ou 4 linhas. */
export const BOARD_ANSWER_MAX = 700;
export const BOARD_MAX_PEOPLE = 12;

export const entrevistadoSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{4,40}$/),
  name: z.string().trim().max(80).default(''),
  role: z.string().trim().max(80).default(''),
  answers: z.partialRecord(z.enum(BOARD_QUESTION_IDS), z.string().max(BOARD_ANSWER_MAX + 200)).default({}),
});
export const boardSchema = z.object({
  entrevistados: z.array(entrevistadoSchema).max(BOARD_MAX_PEOPLE).default([]),
});
export type Entrevistado = z.infer<typeof entrevistadoSchema>;
export type Board = z.infer<typeof boardSchema>;

const has = (v: string | undefined) => !!v && v.trim().length > 0;

/** Perguntas respondidas por pelo menos uma pessoa. */
export function boardCoverage(b: Board): { done: number; total: number } {
  return { done: BOARD_QUESTION_IDS.filter(q => b.entrevistados.some(e => has(e.answers?.[q]))).length, total: BOARD_QUESTION_IDS.length };
}
export const answeredBy = (e: Entrevistado) => BOARD_QUESTION_IDS.filter(q => has(e.answers?.[q])).length;
export const boardAnswers = (b: Board) => b.entrevistados.reduce((n, e) => n + answeredBy(e), 0);
/** Quantas respostas o ADS pede antes de ler as entrevistas. */
export const BOARD_MIN_ANSWERS = 5;
